import { buildMowingPlan } from '@/lib/mowing-plan'
import type {
  CreateLawnGridOptions,
  CreateSampleLawnOptions,
  GameMetrics,
  GridPoint,
  LawnCell,
  LawnCoverage,
  LawnGrid,
  MowerPathInput,
  MowerSampleInput,
  PatternGuideInput,
  StripeDirection,
} from './types'

export const LAWN_STRIPE_COMPLETION_THRESHOLD = 0.95
export const SAMPLE_LAWN_WIDTH = 24
export const SAMPLE_LAWN_HEIGHT = 18

export const SAMPLE_LAWN_REGIONS = [
  { id: 'tree', kind: 'obstacle', x: 2, y: 2, width: 3, height: 3 },
  { id: 'flower-bed', kind: 'excluded', x: 17, y: 2, width: 5, height: 3 },
  { id: 'patio', kind: 'excluded', x: 19, y: 13, width: 5, height: 5 },
  { id: 'sprinkler', kind: 'obstacle', x: 8, y: 13, width: 1, height: 1 },
] as const

const EMPTY_METRICS: GameMetrics = {
  distanceCells: 0,
  deckWidthDistanceCells: 0,
  pathSegments: 0,
  grassContacts: 0,
  overlapHits: 0,
  sharpTurnEvents: 0,
  scalpHits: 0,
  obstacleHits: 0,
  lastHeadingDegrees: null,
  lastPathEnd: null,
  lastPathGrassCellKeys: [],
}

function assertPositiveInteger(value: number, label: string) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new Error(`${label} must be a positive integer.`)
  }
}

function assertFinitePoint(point: GridPoint, label: string) {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    throw new Error(`${label} must contain finite coordinates.`)
  }
}

function round(value: number, decimals: number) {
  const factor = 10 ** decimals
  return Math.round((value + Number.EPSILON) * factor) / factor
}

function normalize360(angle: number) {
  return ((angle % 360) + 360) % 360
}

function normalizeAxial(angle: number) {
  return normalize360(angle) % 180
}

function parity(value: number) {
  return ((value % 2) + 2) % 2
}

export function getPatternGuide({
  pattern,
  x,
  y,
  stripeWidthCells = 3,
}: PatternGuideInput): StripeDirection {
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    throw new Error('Pattern guide coordinates must be finite.')
  }
  if (!Number.isFinite(stripeWidthCells) || stripeWidthCells <= 0) {
    throw new Error('stripeWidthCells must be greater than zero.')
  }

  if (pattern === 'diagonal') {
    const lane = Math.floor((y - x) / stripeWidthCells)
    return { angleDegrees: 45, tone: parity(lane) === 0 ? 'light' : 'dark' }
  }

  if (pattern === 'checkerboard') {
    const blockX = Math.floor(x / stripeWidthCells)
    const blockY = Math.floor(y / stripeWidthCells)
    const lightBlock = parity(blockX + blockY) === 0
    return {
      angleDegrees: lightBlock ? 0 : 90,
      tone: lightBlock ? 'light' : 'dark',
    }
  }

  const lane = Math.floor(y / stripeWidthCells)
  return { angleDegrees: 0, tone: parity(lane) === 0 ? 'light' : 'dark' }
}

export function createLawnGrid(options: CreateLawnGridOptions): LawnGrid {
  assertPositiveInteger(options.width, 'width')
  assertPositiveInteger(options.height, 'height')
  if (!options.runId.trim()) throw new Error('runId must not be empty.')

  const completionThreshold = options.completionThreshold ?? LAWN_STRIPE_COMPLETION_THRESHOLD
  if (!Number.isFinite(completionThreshold) || completionThreshold <= 0 || completionThreshold > 1) {
    throw new Error('completionThreshold must be greater than zero and at most one.')
  }
  const stripeWidthCells = options.stripeWidthCells ?? 3
  if (!Number.isFinite(stripeWidthCells) || stripeWidthCells <= 0) {
    throw new Error('stripeWidthCells must be greater than zero.')
  }

  const cells: LawnCell[][] = Array.from({ length: options.height }, (_, y) =>
    Array.from({ length: options.width }, (_, x) => ({
      x,
      y,
      kind: 'grass' as const,
      cutCount: 0,
      cutDirection: null,
      lastCutHeightInches: null,
    })),
  )

  for (const region of options.regions ?? []) {
    assertPositiveInteger(region.width, `Region ${region.id} width`)
    assertPositiveInteger(region.height, `Region ${region.id} height`)
    if (!Number.isInteger(region.x) || !Number.isInteger(region.y)) {
      throw new Error(`Region ${region.id} coordinates must be integers.`)
    }
    if (
      region.x < 0 ||
      region.y < 0 ||
      region.x + region.width > options.width ||
      region.y + region.height > options.height
    ) {
      throw new Error(`Region ${region.id} must fit inside the lawn.`)
    }

    for (let y = region.y; y < region.y + region.height; y += 1) {
      for (let x = region.x; x < region.x + region.width; x += 1) {
        if (cells[y][x].kind !== 'grass') {
          throw new Error(`Region ${region.id} overlaps another non-grass region.`)
        }
        cells[y][x] = {
          ...cells[y][x],
          kind: region.kind,
          featureId: region.id,
        }
      }
    }
  }

  const grass = options.grass ?? 'bermuda'
  const season = options.season ?? 'summer'
  const mower = options.mower ?? 'rotary'
  const plan = buildMowingPlan({ grass, season, mower })

  return {
    runId: options.runId,
    width: options.width,
    height: options.height,
    cells,
    mode: options.mode ?? 'zen',
    pattern: options.pattern ?? 'straight',
    completionThreshold,
    stripeWidthCells,
    careProfile: {
      grass,
      season,
      mower,
      targetHeightInches: plan.idealH,
      minimumHeightInches: plan.grass.minH,
      maximumHeightInches: plan.grass.maxH,
    },
    metrics: { ...EMPTY_METRICS },
  }
}

export function createSampleLawn(options: CreateSampleLawnOptions): LawnGrid {
  return createLawnGrid({
    ...options,
    width: SAMPLE_LAWN_WIDTH,
    height: SAMPLE_LAWN_HEIGHT,
    regions: SAMPLE_LAWN_REGIONS.map((region) => ({ ...region })),
  })
}

export function getCell(grid: LawnGrid, x: number, y: number): LawnCell | undefined {
  if (!Number.isInteger(x) || !Number.isInteger(y)) return undefined
  return grid.cells[y]?.[x]
}

export function getCoverage(grid: LawnGrid): LawnCoverage {
  let cutCells = 0
  let totalCells = 0

  for (const row of grid.cells) {
    for (const cell of row) {
      if (cell.kind !== 'grass') continue
      totalCells += 1
      if (cell.cutCount > 0) cutCells += 1
    }
  }

  const ratio = totalCells === 0 ? 1 : cutCells / totalCells
  return {
    cutCells,
    totalCells,
    ratio,
    percent: round(ratio * 100, 2),
  }
}

export function isComplete(grid: LawnGrid): boolean {
  return getCoverage(grid).ratio + Number.EPSILON >= grid.completionThreshold
}

function pointToSegmentDistance(point: GridPoint, from: GridPoint, to: GridPoint) {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const lengthSquared = dx * dx + dy * dy
  if (lengthSquared === 0) return Math.hypot(point.x - from.x, point.y - from.y)

  const projection = ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared
  const t = Math.max(0, Math.min(1, projection))
  return Math.hypot(point.x - (from.x + t * dx), point.y - (from.y + t * dy))
}

function headingBetween(from: GridPoint, to: GridPoint) {
  return normalize360((Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI)
}

function turnAngle(from: number, to: number) {
  const difference = Math.abs(normalize360(to) - normalize360(from))
  return Math.min(difference, 360 - difference)
}

function pointsMeet(a: GridPoint | null, b: GridPoint) {
  return a !== null && Math.hypot(a.x - b.x, a.y - b.y) <= 1e-6
}

export function applyMowerPath(grid: LawnGrid, input: MowerPathInput): LawnGrid {
  assertFinitePoint(input.from, 'from')
  assertFinitePoint(input.to, 'to')
  const deckWidthCells = input.deckWidthCells ?? 1
  if (!Number.isFinite(deckWidthCells) || deckWidthCells <= 0) {
    throw new Error('deckWidthCells must be greater than zero.')
  }
  const cutHeightInches = input.cutHeightInches ?? grid.careProfile.targetHeightInches
  if (!Number.isFinite(cutHeightInches) || cutHeightInches <= 0) {
    throw new Error('cutHeightInches must be greater than zero.')
  }

  const distanceCells = Math.hypot(input.to.x - input.from.x, input.to.y - input.from.y)
  const headingDegrees = input.headingDegrees ??
    (distanceCells > Number.EPSILON
      ? headingBetween(input.from, input.to)
      : grid.metrics.lastHeadingDegrees ?? 0)
  if (!Number.isFinite(headingDegrees)) throw new Error('headingDegrees must be finite.')
  const orientedHeading = normalize360(headingDegrees)
  const cutDirection: StripeDirection = {
    angleDegrees: normalizeAxial(orientedHeading),
    tone: orientedHeading < 180 ? 'light' : 'dark',
  }

  const radius = deckWidthCells / 2
  const minX = Math.max(0, Math.floor(Math.min(input.from.x, input.to.x) - radius))
  const maxX = Math.min(grid.width - 1, Math.ceil(Math.max(input.from.x, input.to.x) + radius) - 1)
  const minY = Math.max(0, Math.floor(Math.min(input.from.y, input.to.y) - radius))
  const maxY = Math.min(grid.height - 1, Math.ceil(Math.max(input.from.y, input.to.y) + radius) - 1)
  const cells = grid.cells.map((row) => [...row])
  const obstacleFeatures = new Set<string>()
  const moving = distanceCells > Number.EPSILON
  const joinsPreviousSegment = moving &&
    pointsMeet(grid.metrics.lastPathEnd, input.from) &&
    grid.metrics.lastHeadingDegrees !== null &&
    turnAngle(grid.metrics.lastHeadingDegrees, orientedHeading) <= 90 + 1e-9
  const previousSegmentGrassCells = joinsPreviousSegment
    ? new Set(grid.metrics.lastPathGrassCellKeys)
    : new Set<string>()
  const currentSegmentGrassCellKeys: string[] = []
  let grassContacts = 0
  let overlapHits = 0

  for (let y = minY; y <= maxY; y += 1) {
    for (let x = minX; x <= maxX; x += 1) {
      const center = { x: x + 0.5, y: y + 0.5 }
      if (pointToSegmentDistance(center, input.from, input.to) > radius + 1e-9) continue
      const cell = cells[y][x]
      if (cell.kind === 'obstacle') {
        obstacleFeatures.add(cell.featureId ?? `${x}:${y}`)
        continue
      }
      if (cell.kind !== 'grass') continue

      const cellKey = `${x}:${y}`
      const continuedContact = previousSegmentGrassCells.has(cellKey)
      currentSegmentGrassCellKeys.push(cellKey)
      if (!continuedContact) {
        grassContacts += 1
        if (cell.cutCount > 0) overlapHits += 1
      }
      cells[y][x] = {
        ...cell,
        cutCount: cell.cutCount + (continuedContact ? 0 : 1),
        cutDirection: { ...cutDirection },
        lastCutHeightInches: cutHeightInches,
      }
    }
  }

  const sharpTurn = moving &&
    grid.metrics.lastHeadingDegrees !== null &&
    turnAngle(grid.metrics.lastHeadingDegrees, orientedHeading) >= 70

  return {
    ...grid,
    cells,
    metrics: {
      distanceCells: grid.metrics.distanceCells + distanceCells,
      deckWidthDistanceCells:
        grid.metrics.deckWidthDistanceCells + distanceCells * deckWidthCells,
      pathSegments: grid.metrics.pathSegments + (moving ? 1 : 0),
      grassContacts: grid.metrics.grassContacts + grassContacts,
      overlapHits: grid.metrics.overlapHits + overlapHits,
      sharpTurnEvents: grid.metrics.sharpTurnEvents + (sharpTurn ? 1 : 0),
      scalpHits:
        grid.metrics.scalpHits +
        (cutHeightInches < grid.careProfile.minimumHeightInches ? grassContacts : 0),
      obstacleHits: grid.metrics.obstacleHits + obstacleFeatures.size,
      lastHeadingDegrees: moving ? orientedHeading : grid.metrics.lastHeadingDegrees,
      lastPathEnd: moving ? { ...input.to } : null,
      lastPathGrassCellKeys: moving ? currentSegmentGrassCellKeys : [],
    },
  }
}

export function applyMowerSample(grid: LawnGrid, input: MowerSampleInput): LawnGrid {
  return applyMowerPath(grid, {
    from: input.position,
    to: input.position,
    headingDegrees: input.headingDegrees,
    deckWidthCells: input.deckWidthCells,
    cutHeightInches: input.cutHeightInches,
  })
}

/**
 * Produces the polished completion view without changing played coverage or score inputs.
 * Uncut grass receives the selected pattern's direction, but its cut count remains zero.
 */
export function resolveRemainingGrassPattern(grid: LawnGrid): LawnGrid {
  return {
    ...grid,
    cells: grid.cells.map((row) =>
      row.map((cell) => {
        if (cell.kind !== 'grass' || cell.cutCount > 0) return cell
        return {
          ...cell,
          cutDirection: getPatternGuide({
            pattern: grid.pattern,
            x: cell.x,
            y: cell.y,
            stripeWidthCells: grid.stripeWidthCells,
          }),
          lastCutHeightInches: grid.careProfile.targetHeightInches,
        }
      }),
    ),
  }
}

export function resetLawnGrid(grid: LawnGrid): LawnGrid {
  return {
    ...grid,
    cells: grid.cells.map((row) =>
      row.map((cell) => ({
        ...cell,
        cutCount: 0,
        cutDirection: null,
        lastCutHeightInches: null,
      })),
    ),
    metrics: { ...EMPTY_METRICS },
  }
}
